import { api_configuration } from "../../../common";
import { sha256 } from "../../front 2 state/hashCode";
import { notify_node_update } from "../../state/events";
import { setStickyNumberValue, setStickyTextValue } from "../../state/mapCommandParameters";
import { validateText } from "../../state/textValidation";
import { removeFileFromQueue } from "./fileUpload";
import client_read_api = api_configuration.types.client_state.alan_api;
import client_decorator = api_configuration.types.client_state.alan_api;
import { getGuiText } from "../../guiModel";
import { raw_state_name } from "../../utils";
import { FileToken } from "../../state/expressions";
import { setModified } from "../../state/SaveModifiedStatus";

type UpdatableObject = {
	updating: boolean;
};

export function is_updating(property: client_read_api.Cproperty): boolean {
	const updatable_object = property as any as UpdatableObject;
	if (updatable_object.updating) {
		return true;
	} else {
		updatable_object.updating = true;
		return false;
	}
}

export function stop_updating(property: client_read_api.Cproperty) {
	const updatable_object = property as any as UpdatableObject;
	updatable_object.updating = false;
}

function lookupUserName(node: client_read_api.Cnode) {
	const entry = node.input.entity().cast("entry");
	return entry.properties.key.ref.properties.is_set.switch({
		no: "",
		yes: ($) => $.properties.text,
	});
}

type TextPropertySetBy = client_decorator.Tyes__is_set__text__type__property["by"];
function set_text_value(
	text_property: client_read_api.Ctext__type__property,
	value: string,
	modified: boolean,
	by: TextPropertySetBy
) {
	text_property.root.transact(() => {
		text_property.properties.is_editable.switch({
			no: undefined,
			yes: ($) => {
				if (modified) {
					const is_modified = $.properties.modified.ensure("yes", {
						value: value,
						saving: ["no", {}],
					});
					setModified(is_modified);
					if (is_modified.properties.value !== value) {
						is_modified.set_value(value);
					}
				} else {
					$.properties.modified.ensure("no", {});
				}
			},
		});
		const is_set = text_property.properties.is_set.ensure("yes", {
			text: value,
			by: by,
		});
		if (is_set.properties.text !== value) {
			is_set.set_text(value);
		}
		is_set.properties.by.ensure(raw_state_name(by), {});
		switch (is_set.properties.by.state.name) {
			case "engine":
				break;
			case "user":
				setStickyTextValue(text_property.inferences.text_type(), value);
				break;
			default:
				unexpectedState(is_set.properties.by.state);
		}
	});
}

export function password(text_property: client_read_api.Cno__navigable, value: string): void {
	const mutable_text = text_property.parent;
	mutable_text.properties.is_valid.set("yes", {});
	const user_name = lookupUserName(text_property.parent.parent.parent.parent);
	sha256(user_name + value).then((hash) => {
		set_text_value(text_property.parent, hash, true, "user");
		getGuiText(text_property.parent.inferences.text_type())?.properties.validation.switch({
			none: undefined,
			custom: (custom) => {
				if (validateText(value, custom.properties.rules)) {
					mutable_text.properties.is_valid.set("yes", {});
				} else {
					mutable_text.properties.is_valid.set("no", {
						"invalid rules": "no",
						"key is not unique": "no",
						"reference not resolved": "no",
						"referenced node not valid": "no",
						"text does not match pattern": [
							"yes",
							{
								pattern: custom.properties.rules.properties.regular_expression,
							},
						],
					});
				}
			},
			model: (model) => {
				const rules = model.inferences.text_with_validation().properties.rules;
				if (validateText(value, rules)) {
					mutable_text.properties.is_valid.set("yes", {});
				} else {
					mutable_text.properties.is_valid.set("no", {
						"invalid rules": "no",
						"key is not unique": "no",
						"reference not resolved": "no",
						"referenced node not valid": "no",
						"text does not match pattern": [
							"yes",
							{
								pattern: rules.properties.regular_expression,
							},
						],
					});
				}
			},
		});
	});
}

type NumberPropertySetBy = client_decorator.Tyes__is_set__number__type__property["by"];
export function number(
	number_property: client_read_api.Cnumber__type__property,
	value: number,
	by: NumberPropertySetBy
): void {
	if (
		number_property.properties.backend_known.switch({
			no: false,
			yes: ($) =>
				$.properties.is_readable.switch({
					no: false,
					yes: ($) => $.properties.number.value === value,
				}),
		})
	) {
		number_toBackendValue(number_property);
	} else {
		if (is_updating(number_property.parent)) {
			return;
		}
		const value_is_a_number = !isNaN(value) && value === Math.round(value);
		if (value_is_a_number) {
			switch (number_property.properties.is_editable.state.name) {
				case "no":
					break;
				case "yes": {
					const yes_is_editable = number_property.properties.is_editable.state.node;
					const is_modified = yes_is_editable.properties.modified.set("yes", {
						number: value,
					});
					setModified(is_modified);
					number_property.properties.is_set.set("yes", {
						number: value,
						by: by,
					});
					const is_set = number_property.properties.is_set.cast("yes");
					switch (is_set.properties.by.state.name) {
						case "engine":
							break;
						case "user": {
							setStickyNumberValue(number_property.inferences.number_type(), value);
							break;
						}
						default:
							unexpectedState(is_set.properties.by.state);
					}

					break;
				}
				default:
					unexpectedState(number_property.properties.is_editable.state);
			}
		} else {
			number_property.properties.is_set.set("no", {});
		}
		notifyEntityUpdate(number_property.parent);
		stop_updating(number_property.parent);
	}
}

export function number_toBackendValue(number_property: client_read_api.Cnumber__type__property): void {
	number_property.properties.backend_known.switch({
		no: undefined,
		yes: function (backend_known) {
			backend_known.properties.is_readable.switch({
				no: function () {
					number_property.properties.is_set.set("no", {});
				},
				yes: function ($) {
					number_property.properties.is_set.set("yes", {
						number: $.properties.number.value,
						by: ["engine", {}],
					});
				},
			});
		},
	});
	number_property.properties.is_editable.switch({
		no: undefined,
		yes: function ($) {
			$.properties.modified.set("no", {});
		},
	});
	notifyEntityUpdate(number_property.parent);
}

export function unset_number(number_property: client_read_api.Cnumber__type__property): void {
	const unset = () => {
		number_property.properties.is_editable.switch({
			yes: ($) => {
				$.properties.modified.ensure("no", {});
			},
			no: undefined,
		});
		number_property.properties.is_set.ensure("no", {});
	};
	number_property.properties.backend_known.switch({
		yes: ($) =>
			$.properties.is_readable.switch({
				no: () => {
					unset();
				},
				yes: () => {
					number_toBackendValue(number_property);
				},
			}),
		no: () => {
			unset();
		},
	});
}

export function file_toBackendValue(
	file_backend_value: client_read_api.Cyes__is_readable__yes__backend_known__file
): void {
	const mutable_file = file_backend_value.parent.parent;
	mutable_file.properties.is_set.set("yes", {
		token: file_backend_value.properties.token,
		extension: file_backend_value.properties.extension,
	});
	mutable_file.properties.is_editable.switch({
		no: undefined,
		yes: function ($) {
			$.properties.modified.ensure("no", {});
		},
	});
	removeFileFromQueue(file_backend_value.parent.parent);
}

export function fileToken(file_property: client_read_api.Cfile__type__property, file_token: FileToken): void {
	switch (file_property.properties.is_editable.state.name) {
		case "no":
			break;
		case "yes": {
			const yes_is_editable = file_property.properties.is_editable.state.node;
			yes_is_editable.properties.modified.set("yes", {
				extension: file_token.extension,
				"upload status": [
					"done",
					{
						token: file_token.token,
					},
				],
			});
			file_property.properties.is_set.set("yes", {
				token: file_token.token,
				extension: file_token.extension,
			});
			break;
		}
	}
}

export function reportNumberParameter(number: client_read_api.Cnumber__type__parameters, value: number): void {
	number.properties.is_set.set("yes", {
		value: value,
	});
	number.properties.is_valid.set("yes", {});
	evaluateReportValidity(number.parent.parent);
}

export function reportTextParameter(text: client_read_api.Ctext__type__parameters, value: string): void {
	text.properties.is_set.set("yes", {
		value: value,
	});
	text.properties.is_valid.set("yes", {});
	evaluateReportValidity(text.parent.parent);
}

export function evaluateReportValidity(parameters: client_read_api.Creport_parameters): void {
	let valid = true;
	parameters.properties.parameters.forEach((parameter) => {
		valid =
			valid &&
			parameter.properties.type.switch<boolean>({
				number: ($) =>
					$.properties.is_valid.switch({
						no: false,
						yes: true,
					}),
				text: ($) =>
					$.properties.is_valid.switch({
						no: false,
						yes: true,
					}),
			});
	});
	if (valid) {
		parameters.properties.is_valid.set("yes", {});
	} else {
		parameters.properties.is_valid.set("no", {});
	}
}

export function notifyEntityUpdate(property: client_read_api.Cproperty) {
	notify_node_update(property.parent.parent.input.entity().input.node());
}
